import { useRef, useCallback, FC } from 'react';
import ReactQuill, { ReactQuillProps } from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { useContextNotifications } from '../../../context/notifications';
import { useUserFileUpload } from '../../../hooks/users';

type Props = ReactQuillProps & {
  hideToolbar?: boolean;
}

const RichTextEditor: FC<Props> = ({
  onChange = () => null,
  placeholder = '',
  readOnly = false,
  hideToolbar = false,
  ...rest
})=> {
  const ref = useRef<ReactQuill>(null);

  const { openNotification } = useContextNotifications();
  const fileUpload = useUserFileUpload();

  const imageHandler = useCallback(() => {
    const input = document.createElement("input");

    input.setAttribute("type", "file");
    input.setAttribute("accept", "image/*");
    input.click();

    input.onchange = async () => {
      if (input !== null && input.files !== null) {
        const file = input.files[0];

        const formData = new FormData();

        formData.append("file", file);

        fileUpload.fetch(formData).then((result) => {
          const quill = ref.current;

          if (quill && result) {
            const range = quill.getEditorSelection();

            if (range) {
              quill.getEditor().insertEmbed(range.index, "image", result.data)
            }
          }
        }).catch((error) => {
            openNotification?.({
              message: `Error while uploading image: ${error?.message}`,
              type: 'error',
            });
          });

      }
    };
  }, []);

  const modules = hideToolbar
    ? { toolbar: false }
    : {
    toolbar: {
      container: [
        [{ size: [] }],
        [{ font: [] }],
        [{ header: [1, 2, 3, 4, 5, 6, false] }],
        [{ align: [] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ color: [] }, { background: [] }],
        [{ script: 'sub' }, { script: 'super' }],
        ['blockquote', 'code-block'],
        [{ list: 'ordered' }, { list: 'bullet' }],
        [{ indent: '-1' }, { indent: '+1' }, { align: [] }],
        ['link', 'image', 'video'],
      ],
        handlers: {
        image: imageHandler,
      },
    },
  };

  return (
    <ReactQuill
      ref={ref}
      theme="snow"
      placeholder={placeholder}
      modules={modules}
      onChange={onChange}
      readOnly={readOnly}
      {...rest}
    />
  )
}

export default RichTextEditor;
